// <auto-generated>
// This file is automatically generated for policy configuration.
// Do not modify this file directly. Changes will be overwritten.
// Generated: 2026-03-06T21:49:18.803Z
// </auto-generated>

import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, FiberManualRecord } from '@mui/icons-material';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel, cancelShortcutLabel } from '@/hooks/useSaveShortcut';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { getErrorMessage } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';
import { ruleService } from '@/services/core/RuleService';
import type { ConditionDto, RuleContextSchemaDto } from '@/types/core/Rules';
import { usePolicyConfiguration, FieldCatalogItemDto, PolicySetDto, TransitionDto } from '@/core/hooks/usePolicyConfiguration';

// C7: the same RAQB-based editor Business Rules use, lazy-loaded (react-awesome-query-builder is a
// large chunk) and only rendered for ConditionalRequiredFields policies.
const ConditionEditor = lazy(() =>
  import('@/core/rules/components/ConditionEditor').then((m) => ({ default: m.ConditionEditor }))
);

const CONDITIONAL_POLICY_TYPE = 'ConditionalRequiredFields';

/**
 * C7: a ConditionalRequiredFields condition may only read the entity's OWN catalog fields (the
 * server rejects anything else, and the browser checklist evaluates against form data). Narrow the
 * rules context schema to the entity object and to fields present in the policy field catalog.
 */
function toPolicyConditionSchema(
  schema: RuleContextSchemaDto | undefined,
  entityName: string,
  fieldCatalog: FieldCatalogItemDto[] | undefined
): RuleContextSchemaDto | null {
  if (!schema || !fieldCatalog) return null;
  const catalogPaths = new Set(fieldCatalog.map((f) => f.path.toLowerCase()));
  const entityObject = schema.objects.find((o) => o.name === entityName);
  if (!entityObject) return null;
  const fields = entityObject.fields.filter((f) => {
    const member = f.path.split('.').pop() ?? '';
    return catalogPaths.has(member.toLowerCase());
  });
  return { ...schema, objects: [{ ...entityObject, fields }] };
}

/**
 * Policy Configuration Detail View
 * Master-detail layout: transitions list on the left, field checkboxes on the right
 */
export function PolicyConfigurationDetailView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { getPolicySet, getSupportedEntities, getFieldCatalog, getTransitions, createOrUpdatePolicySet, isSaving } = usePolicyConfiguration();

  const { data: existingPolicy, isLoading: isLoadingPolicy } = getPolicySet(isNew ? '' : (id || ''));
  const { data: supportedEntities } = getSupportedEntities();

  const [entityName, setEntityName] = useState<string>('');
  const [policyType, setPolicyType] = useState<string>('RequiredFieldsOnTransition');
  const [selectedTransition, setSelectedTransition] = useState<string>('');
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  // C7: per "transition:field" key, the AST condition that makes the field required.
  const [conditions, setConditions] = useState<Map<string, ConditionDto | null>>(new Map());
  const [orgUnitId, setOrgUnitId] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const isConditional = policyType === CONDITIONAL_POLICY_TYPE;

  const { data: fieldCatalog, isLoading: isLoadingCatalog } = getFieldCatalog(entityName);
  const { data: transitions, isLoading: isLoadingTransitions } = getTransitions(entityName);

  const { data: contextSchema } = useQuery({
    queryKey: ['context-schema', entityName, undefined],
    queryFn: () => ruleService.getContextSchema(entityName),
    enabled: isConditional && !!entityName,
    staleTime: CACHE_TIME.METADATA,
  });
  const conditionSchema = useMemo(
    () => toPolicyConditionSchema(contextSchema, entityName, fieldCatalog),
    [contextSchema, entityName, fieldCatalog]
  );

  const entities = supportedEntities || [];

  // Load existing policy data
  useEffect(() => {
    if (existingPolicy) {
      setEntityName(existingPolicy.entityName || '');
      setPolicyType(existingPolicy.policyType || 'RequiredFieldsOnTransition');
      setOrgUnitId(existingPolicy.orgUnitId || '');
      const fields = new Set<string>();
      const loadedConditions = new Map<string, ConditionDto | null>();
      existingPolicy.rules?.forEach((rule: any) => {
        if (rule.isRequired) {
          const key = `${rule.transitionKey}:${rule.fieldPath}`;
          fields.add(key);
          loadedConditions.set(key, rule.condition ? (JSON.parse(rule.condition) as ConditionDto) : null);
        }
      });
      setSelectedFields(fields);
      setConditions(loadedConditions);
    }
  }, [existingPolicy]);

  // Count configured fields per transition
  const configuredTransitions = useMemo(() => {
    const counts = new Map<string, number>();
    selectedFields.forEach((key) => {
      const transitionKey = key.split(':')[0];
      counts.set(transitionKey, (counts.get(transitionKey) || 0) + 1);
    });
    return counts;
  }, [selectedFields]);

  // Auto-select first transition when transitions load
  useEffect(() => {
    if (transitions?.length > 0 && !selectedTransition) {
      setSelectedTransition(transitions[0].key);
    }
  }, [transitions]);

  const handleFieldToggle = (transitionKey: string, fieldPath: string) => {
    const key = `${transitionKey}:${fieldPath}`;
    const newSelected = new Set(selectedFields);
    if (newSelected.has(key)) {
      newSelected.delete(key);
      const newConditions = new Map(conditions);
      newConditions.delete(key);
      setConditions(newConditions);
    } else {
      newSelected.add(key);
    }
    setSelectedFields(newSelected);
  };

  const handleConditionChange = (key: string, condition: ConditionDto | null) => {
    const newConditions = new Map(conditions);
    newConditions.set(key, condition);
    setConditions(newConditions);
  };

  // C7: a conditional policy is only saveable when every selected field carries a condition —
  // the server refuses the save otherwise, so the button says so first. An EMPTY selection is a
  // valid save: it clears the tenant's rules while keeping the set, which is how a tenant opts
  // out of the seeded defaults (a deleted set would be re-seeded).
  const missingConditionCount = isConditional
    ? Array.from(selectedFields).filter((key) => !conditions.get(key)).length
    : 0;
  const canSave = !isSaving && !!entityName && missingConditionCount === 0;

  const handleSave = () => {
    const rules = Array.from(selectedFields).map((key) => {
      const [transitionKey, fieldPath] = key.split(':');
      const condition = isConditional ? conditions.get(key) : null;
      return {
        transitionKey,
        fieldPath,
        isRequired: true,
        condition: condition ? JSON.stringify(condition) : undefined,
      };
    });

    setSaveError(null);
    createOrUpdatePolicySet(
      {
        id: isNew ? undefined : id,
        entityName,
        policyType,
        orgUnitId: orgUnitId || undefined,
        isActive: true,
        rules,
      },
      {
        // Stay on the view: the hook invalidates the detail query, so the form re-seeds from the
        // saved policy. A new policy lands on its id route (replace) so the next save is an update.
        onSuccess: (saved: PolicySetDto) => {
          setSavedAt(Date.now());
          if (isNew) {
            navigate('/core/policies/' + saved.id, { replace: true });
          }
        },
        // Strict authoring: the server rejects unknown fields and conditions it cannot evaluate.
        onError: (error: unknown) => setSaveError(getErrorMessage(error)),
      }
    );
  };

  const handleCancel = () => {
    navigate('/core/policies');
  };

  if (!isNew && isLoadingPolicy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Group fields by section
  const fieldsBySection = new Map<string, FieldCatalogItemDto[]>();
  fieldCatalog?.forEach((field: FieldCatalogItemDto) => {
    const section = field.section || 'General';
    if (!fieldsBySection.has(section)) {
      fieldsBySection.set(section, []);
    }
    fieldsBySection.get(section)!.push(field);
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <ViewToolbar onBack={handleCancel} onSave={handleSave} saveDisabled={!canSave} onCancel={handleCancel} cancelDisabled={isSaving}>
        <Tooltip title={cancelShortcutLabel()}>
          <span>
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                bgcolor: 'grey.500',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                '&:hover': { bgcolor: 'grey.600' }
              }}
            >
              {t('common.cancel')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canSave}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
              }}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2, bgcolor: 'background.default' }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          {isNew ? t('policy.newPolicy') : t('policy.editPolicy', { entity: entityName })}
        </Typography>

        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {/* Policy Details */}
        <Paper sx={{ p: 1.5, mb: 1.5 }} elevation={1}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mb: 0.5, fontSize: '0.8125rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
            {t('policy.details')}
          </Typography>
          <JsonFormsThemeProvider>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small" variant="standard">
                  <InputLabel>{t('policy.colEntity')}</InputLabel>
                  <Select
                    value={entityName}
                    onChange={(e) => {
                      setEntityName(e.target.value);
                      setSelectedTransition('');
                      setSelectedFields(new Set());
                    }}
                    label={t('policy.colEntity')}
                    disabled={!isNew}
                  >
                    {entities.map((entity) => (
                      <MenuItem key={entity} value={entity}>
                        {entity}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small" variant="standard">
                  <InputLabel>{t('policy.colPolicyType')}</InputLabel>
                  <Select
                    value={policyType}
                    onChange={(e) => setPolicyType(e.target.value)}
                    label={t('policy.colPolicyType')}
                  >
                    <MenuItem value="RequiredFieldsOnTransition">
                      {t('policy.typeRequiredFieldsOnTransition')}
                    </MenuItem>
                    <MenuItem value={CONDITIONAL_POLICY_TYPE}>
                      {t('policy.typeConditionalRequiredFields')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  label={t('policy.orgUnitId')}
                  value={orgUnitId}
                  onChange={(e) => setOrgUnitId(e.target.value)}
                  helperText={t('policy.orgUnitHelp')}
                />
              </Grid>
            </Grid>
          </JsonFormsThemeProvider>
        </Paper>

        {/* Master-Detail: Transitions list + Field checkboxes */}
        {entityName && (
          <Box sx={{ display: 'flex', flex: 1, gap: 1.5, overflow: 'hidden', minHeight: 0 }}>
            {/* Left: Transitions list */}
            <Paper
              variant="outlined"
              sx={{ width: 360, minWidth: 320, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8125rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
                  {t('policy.transitions')}
                </Typography>
              </Box>
              {isLoadingTransitions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !transitions?.length ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('policy.noTransitions')}
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                  {transitions.map((transition: TransitionDto) => {
                    const count = configuredTransitions.get(transition.key) || 0;
                    const isActive = selectedTransition === transition.key;
                    return (
                      <ListItemButton
                        key={transition.key}
                        selected={isActive}
                        onClick={() => setSelectedTransition(transition.key)}
                        sx={{
                          py: 1,
                          borderLeft: isActive ? '3px solid' : '3px solid transparent',
                          borderLeftColor: isActive ? 'primary.main' : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <FiberManualRecord
                            sx={{
                              fontSize: 10,
                              color: count > 0 ? 'primary.main' : 'action.disabled',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={isActive ? 600 : 400}>
                                {transition.from} → {transition.to}
                              </Typography>
                              {count > 0 && (
                                <Chip
                                  label={count}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={transition.description || null}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Paper>

            {/* Right: Field checkboxes */}
            <Paper
              variant="outlined"
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              {selectedTransition ? (
                <>
                  <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8125rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
                      {t('policy.requiredFields')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('policy.forTransition', { transition: selectedTransition.replace('->', ' → ') })}
                    </Typography>
                  </Box>
                  {isConditional && (
                    <Alert severity={missingConditionCount > 0 ? 'warning' : 'info'} sx={{ mx: 1.5, mb: 1 }}>
                      {missingConditionCount > 0
                        ? t('policy.conditionMissing', { count: missingConditionCount })
                        : t('policy.conditionHelp')}
                    </Alert>
                  )}

                  {isLoadingCatalog ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', px: 0.5 }}>
                      {Array.from(fieldsBySection.entries()).map(([section, fields]) => (
                        <Box key={section} sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ px: 1.5, py: 0.5, fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                            {section}
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {fields.map((field) => {
                              const key = `${selectedTransition}:${field.path}`;
                              const isSelected = selectedFields.has(key);
                              return (
                                <Box key={field.path}>
                                  <ListItemButton
                                    onClick={() => handleFieldToggle(selectedTransition, field.path)}
                                    sx={{ py: 0.25 }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      {isSelected ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank />}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={field.label || field.path}
                                      secondary={field.type}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                      secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                  </ListItemButton>
                                  {isConditional && isSelected && (
                                    <ListItem sx={{ display: 'block', pl: 6, pr: 1.5, py: 0.5 }} data-testid={`policy-condition-${field.path}`}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {t('policy.requiredWhen', { field: field.label || field.path })}
                                      </Typography>
                                      {conditionSchema ? (
                                        <Suspense fallback={<CircularProgress size={18} />}>
                                          <ConditionEditor
                                            value={conditions.get(key) ?? null}
                                            onChange={(condition) => handleConditionChange(key, condition)}
                                            contextSchema={conditionSchema}
                                            enableFieldComparison={false}
                                            compact
                                          />
                                        </Suspense>
                                      ) : (
                                        <CircularProgress size={18} />
                                      )}
                                    </ListItem>
                                  )}
                                </Box>
                              );
                            })}
                          </List>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">
                    {t('policy.selectTransition')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {!entityName && (
          <Alert severity="info">
            {t('policy.selectEntity')}
          </Alert>
        )}
      </Box>

      <Snackbar
        open={savedAt !== null}
        autoHideDuration={3000}
        onClose={() => setSavedAt(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSavedAt(null)}>
          {t('policy.saved')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
